import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useFetch } from '@/shared/hooks/useFetch';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiCargoIdAdminGet,
  apiCargoIdLookupByCode,
  apiCargoIdLookupByQuery,
  apiCargoIdParcelsGet,
  apiCargoIdsAdminGet,
} from '@/entities/cargo-id/modules/api/cargoIds';
import type {
  ApiCargoIdLookupItem,
  ApiCargoIdLookupQueryResponse,
  ApiCargoIdsListParams,
} from '@/shared/types/api/cargo-id';

export const useCargoIds = (params: ApiCargoIdsListParams) =>
  useFetch({
    api: () => apiCargoIdsAdminGet(params),
    keywords: queryKeys.cargoIds.list(params),
    staleTime: 60_000,
  });

export const useCargoIdDetail = (id: string | undefined) =>
  useFetch({
    api: () => apiCargoIdAdminGet(id ?? ''),
    keywords: queryKeys.cargoIds.detail(id ?? ''),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCargoIdParcels = (id: string | undefined, page: number) =>
  useFetch({
    api: () => apiCargoIdParcelsGet(id ?? '', page),
    keywords: queryKeys.cargoIds.parcels(id ?? '', page),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCargoIdLookupByCode = (code: string) =>
  useQuery<ApiCargoIdLookupItem, Error>({
    queryKey: queryKeys.cargoIds.lookupByCode(code),
    queryFn: () => apiCargoIdLookupByCode(code),
    enabled: code.trim().length > 0,
    retry: false,
    staleTime: 60_000,
  });

/**
 * Paginated cargo-id lookup. Keyed by the (already-debounced) search term so different
 * terms cache separately and stale terms are ignored. `getNextPageParam` advances to the
 * next page while the API reports `has_next`. Pages are flattened into a single item list.
 * Pass `enabled` (e.g. dropdown-open state) so no request fires while the select is closed.
 */
export const useInfiniteCargoIdLookup = (q: string, enabled: boolean) => {
  const query = useInfiniteQuery<ApiCargoIdLookupQueryResponse, Error>({
    queryKey: queryKeys.cargoIds.lookupInfinite(q),
    queryFn: ({ pageParam }) => apiCargoIdLookupByQuery(q, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_next ? allPages.length + 1 : undefined,
    enabled,
    retry: false,
    staleTime: 30_000,
  });

  const items = useMemo<ApiCargoIdLookupItem[]>(
    () => (query.data?.pages ?? []).flatMap((page) => page.results),
    [query.data],
  );

  return {
    items,
    isFetching: query.isFetching,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};
